import { createFileRoute } from "@tanstack/react-router";
import type React from "react";
import { useEffect, useState } from "react";

import {
  Box,
  ShieldCheck,
  Clock,
  Zap,
  CheckCircle2,
  Check,
  Package,
  Store,
  Building2,
  FileText,


  MessageCircle,
  Truck,
  MapPin,
  Map as MapIcon,
  ArrowRight,
  Plus,
  PackageCheck,
  PackageSearch,
  ArrowLeftRight,
  Sparkles,
  Send,
  MousePointerClick,
  User,
  Phone,
  ChevronDown,

} from "lucide-react";

import {
  PageHeader,
  PageFooter,
  useRevealOnScroll,
  WA_NUMBER,
} from "@/components/PageLayout";
import { captureTracking, saveLastLead, type LeadSummary } from "@/lib/tracking";

import CoverageMap from "@/components/LazyCoverageMap";
import { PremiumDarkBg } from "@/components/PremiumDarkBg";

import boxesImg from "@/assets/fleet-porto-velho.png.asset.json";
import heroBgImg from "@/assets/fiorino-hero-branded.webp";


export const Route = createFileRoute("/encomendas")({
  head: () => ({
    meta: [
      { title: "Envio de encomendas RO AM AC — Rota Norte Express" },
      {
        name: "description",
        content:
          "Envie encomendas, documentos e mercadorias em Rondônia, Amazonas e Acre pela BR-364 e BR-319. Coleta, transporte e entrega com atendimento das 7h às 20h pelo WhatsApp.",
      },
      { property: "og:title", content: "Envio de encomendas RO AM AC — Rota Norte Express" },
      {
        property: "og:description",
        content:
          "Coleta, transporte e entrega de encomendas em RO, AM e AC. Fale com nossa equipe pelo WhatsApp.",
      },
      { property: "og:url", content: "https://rotanortexpress.com.br/encomendas" },
    ],
    links: [{ rel: "canonical", href: "https://rotanortexpress.com.br/encomendas" }],
  }),
  component: EncomendasPage,
});

function scrollToCotacao() {
  if (typeof document === "undefined") return;
  document.getElementById("cotacao")?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function WhatsappIcon({ className, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      className={className}
      {...props}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.198.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.15-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 0 1-1.511-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.82 11.82 0 0 0 20.463 3.488z" />
    </svg>
  );
}





function EncomendasPage() {
  useRevealOnScroll();
  return (
    <main className="min-h-screen bg-gradient-to-b from-white via-[#F4F7FC] to-white text-foreground">
      <PageHeader />



      {/* HERO */}
      <section className="relative pt-28 sm:pt-36 pb-0 overflow-hidden">
        <PremiumDarkBg glow="right" image={heroBgImg} imagePosition="right center" />


        <div className="relative mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="rne-hero-in text-white text-center lg:text-left">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em]">
              <Package className="h-3.5 w-3.5 text-brand-light" />
              Encomendas
            </span>
            <h1 className="mt-5 font-display text-[42px] sm:text-6xl lg:text-[68px] font-extrabold leading-[1.02] tracking-[-0.035em]">
              Envie suas encomendas
              <br />
              <span className="text-brand-light">com segurança e agilidade</span>
            </h1>
            <span
              aria-hidden
              className="mt-5 inline-block h-[3px] w-16 rounded-full bg-[color:var(--brand-red)]"
            />
            <p className="mt-6 text-[15px] sm:text-[16px] text-white/75 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Coleta, transporte e entrega de encomendas em toda a BR-364 e BR-319. Ideal para
              lojas, empresas e pessoas físicas que precisam de rapidez e cuidado com cada volume.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-center lg:justify-start">
              <button
                type="button"
                onClick={scrollToCotacao}
                className="rne-blue-pulse relative inline-flex items-center justify-center gap-3 h-[52px] px-6 rounded-xl font-semibold text-[14.5px] text-white bg-gradient-to-b from-brand to-brand-dark shadow-[0_18px_40px_-16px_rgba(11,36,71,0.55)] overflow-hidden"
              >
                <Sparkles className="relative h-[18px] w-[18px]" strokeWidth={2.3} />
                <span className="relative">Cotar minha encomenda</span>
                <ArrowRight
                  className="relative h-[17px] w-[17px] opacity-90"
                  strokeWidth={2.4}
                />
              </button>
              <a
                href="#como-funciona"
                className="inline-flex items-center justify-center gap-2 h-[52px] px-6 rounded-xl border border-white/20 bg-white/[0.04] hover:bg-white/[0.10] text-white font-semibold text-[15px] transition-all"
              >
                Como funciona
              </a>
            </div>


            <p className="mt-4 text-[13px] text-white/60 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Postagens até 13h saem no mesmo dia. Após esse horário, seguem no próximo trajeto
              disponível.
            </p>

            <div className="mt-6 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-3 text-[13px] text-white/70">
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-brand-light" /> Encomendas com seguro
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-brand-light" /> Atendimento das 7h às 20h
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-brand-light" /> RO • AM • AC
              </span>
            </div>
          </div>

          <div className="relative rne-3d-frame">
            <div
              className="absolute inset-0 rounded-[36px] bg-gradient-to-br from-brand-light/30 to-brand/10 blur-2xl"
              aria-hidden
            />
            <div className="relative rne-3d-card rounded-[32px] overflow-hidden border border-white/15 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.75)]">
              <img
                src={boxesImg.url}
                alt="Encomendas prontas para envio Rota Norte Express"
                loading="eager"
                fetchPriority="high"
                width={1280}
                height={1024}
                className="w-full h-[360px] sm:h-[460px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#06122B]/40 via-transparent to-transparent" />
              <span aria-hidden className="absolute inset-0 rne-3d-shine pointer-events-none" />
            </div>
          </div>
        </div>
      </section>



      {/* ROTAS — jornada vertical */}
      <section id="rotas" className="relative pt-10 pb-20 sm:pt-12 sm:pb-24 overflow-hidden">
        <PremiumDarkBg glow="left" />
        <div className="relative mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
          <div className="rne-reveal text-center max-w-2xl mx-auto">
            <span className="eyebrow text-white/70">Nossas rotas</span>
            <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-white tracking-tight leading-[1.1]">
              Onde suas encomendas <span className="text-brand-light">chegam</span>
            </h2>
            <p className="mt-4 text-[14.5px] text-white/75 leading-relaxed">
              Três corredores principais conectando RO, AM e AC.
            </p>
          </div>






          <div className="rne-stagger mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <RouteAnimatedCard
              idx={0}
              tag="Rota 1 · BR-364"
              title="Porto Velho → Vilhena"
              sub="Corredor Rondônia"
              data={[
                { c: "Porto Velho", s: "RO", hl: "start" },
                { c: "Candeias do Jamari", s: "RO" },
                { c: "Itapoã do Oeste", s: "RO" },
                { c: "São Pedro", s: "RO" },
                { c: "Ariquemes", s: "RO" },
                { c: "Jaru", s: "RO" },
                { c: "Ouro Preto", s: "RO" },
                { c: "Ji-Paraná", s: "RO" },
                { c: "Presidente Médici", s: "RO" },
                { c: "Cacoal", s: "RO" },
                { c: "Pimenta Bueno", s: "RO" },
                { c: "Guaporé", s: "RO" },
                { c: "Vilhena", s: "RO", hl: "end" },
              ]}
            />
            <RouteAnimatedCard
              idx={1}
              tag="Rota 2 · BR-319"
              title="Porto Velho → Manaus"
              sub="Conexão com o Amazonas"
              data={[
                { c: "Porto Velho", s: "RO", hl: "start" },
                { c: "Humaitá", s: "AM" },
                { c: "Distrito de Realidade", s: "AM" },
                { c: "Careiro", s: "AM" },
                { c: "Vila Rio Araça", s: "AM" },
                { c: "Manaus", s: "AM", hl: "end" },
              ]}
            />
            <RouteAnimatedCard
              idx={2}
              tag="Rota 3 · BR-364"
              title="Porto Velho → Rio Branco"
              sub="Rumo ao Acre"
              data={[
                { c: "Porto Velho", s: "RO", hl: "start" },
                { c: "Jaci Paraná", s: "RO" },
                { c: "Nova Mutum", s: "RO" },
                { c: "Jirau", s: "RO" },
                { c: "Abunã", s: "RO" },
                { c: "Vista Alegre do Abunã", s: "RO" },
                { c: "Extrema", s: "RO" },
                { c: "Nova Califórnia", s: "RO" },
                { c: "Vila Campina", s: "AC" },
                { c: "Rio Branco", s: "AC", hl: "end" },
              ]}
            />
          </div>
        </div>
      </section>


      {/* TRUST BAR — destaques flutuantes */}
      <section className="relative -mt-6 sm:-mt-10 z-10">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[28px] p-[2px] bg-gradient-to-r from-brand-light/40 via-brand-dark/30 to-[color:var(--whatsapp)]/50 shadow-[0_40px_100px_-40px_rgba(7,28,58,0.45)]">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 bg-white rounded-[26px] overflow-hidden">
              {[
                { i: MapPin, n: "+20", t: "Cidades atendidas" },
                { i: Truck, n: "Passageiros e encomendas", t: "Na mesma rota" },
                { i: MapIcon, n: "BR-364 e BR-319", t: "Até Manaus" },
                { i: WhatsappIcon, n: "Atendimento rápido", t: "Pelo WhatsApp", highlight: true },
              ].map(({ i: Icon, n, t, highlight }, idx) => (
                <div
                  key={idx}
                  className={`relative flex items-center gap-4 px-5 sm:px-6 py-6 sm:py-7 transition-colors min-w-0 ${
                    highlight
                      ? "bg-gradient-to-br from-[#25D366] to-[#128C7E] text-white"
                      : "hover:bg-surface/60"
                  } ${
                    idx > 0 && !highlight
                      ? "sm:[&:nth-child(even)]:border-l sm:border-border lg:border-l lg:[&:nth-child(odd)]:border-l"
                      : ""
                  } ${idx >= 2 ? "sm:border-t lg:border-t-0" : ""}`}
                >
                  {highlight && (
                    <span className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-[0.18em] bg-white/20 text-white px-2 py-0.5 rounded-full backdrop-blur">
                      Destaque
                    </span>
                  )}
                  <span
                    className={`h-12 w-12 sm:h-14 sm:w-14 rounded-2xl flex items-center justify-center shrink-0 ${
                      highlight
                        ? "bg-white/15 text-white shadow-[0_10px_24px_-6px_rgba(0,0,0,0.35)]"
                        : "bg-brand-dark/5 text-brand-dark"
                    }`}
                  >
                    <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
                  </span>
                  <div className="leading-tight min-w-0">
                    <div
                      className={`font-display font-extrabold text-[14px] sm:text-[16px] break-words ${
                        highlight ? "text-white" : "text-brand-dark"
                      }`}
                    >
                      {n}
                    </div>
                    <div
                      className={`text-[11px] font-semibold tracking-[0.12em] uppercase mt-1 break-words ${
                        highlight ? "text-white/80" : "text-muted-foreground"
                      }`}
                    >
                      {t}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PARA QUEM É */}
      <section id="para-quem" className="py-20 sm:py-24 bg-[#F4F7FC]">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
          <div className="rne-reveal text-center max-w-2xl mx-auto">
            <span className="eyebrow">Para quem é</span>
            <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
              Feito para quem precisa <span className="text-brand">enviar com confiança</span>
            </h2>
            <p className="mt-4 text-[14.5px] text-muted-foreground leading-relaxed">
              Atendemos diferentes perfis com a mesma dedicação e cuidado em cada envio.
            </p>
          </div>

          <div className="rne-stagger mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { i: Store, t: "Loja / E-commerce", d: "Envios recorrentes de mercadorias para clientes de outras cidades." },
              { i: Building2, t: "Empresas", d: "Logística recorrente, malotes e cargas com prazos combinados." },
              { i: FileText, t: "Documentos", d: "Envio de contratos, notas e materiais com sigilo garantido." },
              { i: Package, t: "Pessoa física", d: "Presentes, mudanças pequenas e itens pessoais entre cidades." },
            ].map(({ i: Icon, t, d }) => (
              <div
                key={t}
                className="bg-white rounded-2xl border border-[#E6ECF5] p-6 shadow-[0_8px_28px_-18px_rgba(11,36,71,0.25)] flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center text-white shadow-soft">
                  <Icon className="h-5 w-5" strokeWidth={2.2} />
                </div>
                <h3 className="mt-5 font-display font-bold text-brand-dark text-[17px] leading-snug">
                  {t}
                </h3>
                <span aria-hidden className="mt-2 block h-[2px] w-10 rounded-full bg-[color:var(--brand-red)]/85" />
                <p className="mt-3 text-[14px] text-muted-foreground leading-relaxed flex-1">{d}</p>
              </div>
            ))}
          </div>

          <div className="rne-reveal mt-12 flex justify-center">
            <a
              href="#cotacao"
              className="inline-flex items-center gap-2 rounded-full bg-brand-dark hover:bg-brand text-white px-7 py-3.5 text-[14px] font-semibold shadow-[0_14px_34px_-12px_rgba(11,60,136,0.55)] hover:shadow-[0_20px_44px_-12px_rgba(11,60,136,0.75)] transition-all duration-300"
            >
              <User className="h-4 w-4" strokeWidth={2.4} />
              Solicitar cotação para meu perfil
              <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
            </a>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section id="como-funciona" className="py-20 sm:py-24">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
          <div className="rne-reveal text-center max-w-2xl mx-auto">
            <span className="eyebrow">Como funciona</span>
            <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
              Em 3 passos simples
            </h2>
          </div>

          <HowItWorksStepper />

          <div className="rne-reveal mt-10 flex justify-center">
            <a
              href="#cotacao"
              className="inline-flex items-center gap-2 rounded-full bg-brand-dark hover:bg-brand text-white px-7 py-3.5 text-[14px] font-semibold shadow-[0_14px_34px_-12px_rgba(11,60,136,0.55)] hover:shadow-[0_20px_44px_-12px_rgba(11,60,136,0.75)] transition-all duration-300"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={2.4} />
              Começar minha cotação
              <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
            </a>
          </div>
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section id="diferenciais" className="py-20 sm:py-24 bg-[#F4F7FC]">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
          <div className="rne-reveal text-center max-w-2xl mx-auto">
            <span className="eyebrow">Diferenciais</span>
            <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
              Por que enviar com a <span className="text-brand">Rota Norte Express</span>
            </h2>
            <p className="mt-4 text-[14.5px] text-muted-foreground leading-relaxed">
              Estrutura, experiência e atendimento pensados para quem depende
              da entrega chegar no prazo — sem burocracia e sem surpresas.
            </p>
          </div>

          <div className="rne-stagger mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                i: ShieldCheck,
                t: "Seguro incluso",
                d: "Toda encomenda segue protegida contra extravio e avarias, sem custo extra.",
              },
              {
                i: Zap,
                t: "Saída no mesmo dia",
                d: "Postagens até as 13h embarcam no próximo horário e seguem em rota imediatamente.",
              },
              {
                i: MapIcon,
                t: "Cobertura BR-364 e BR-319",
                d: "Mais de 20 cidades atendidas entre Vilhena, Porto Velho, Manaus e Rio Branco.",
              },
              {
                i: MessageCircle,
                t: "Atendimento humano",
                d: "Fale direto com nossa equipe pelo WhatsApp, das 7h às 20h, todos os dias.",
              },
              {
                i: PackageCheck,
                t: "Rastreio simples",
                d: "Acompanhe o status da entrega a qualquer momento pelo mesmo canal do pedido.",
              },
              {
                i: Truck,
                t: "Frota própria",
                d: "Veículos revisados e motoristas experientes que conhecem cada quilômetro da rota.",
              },
            ].map(({ i: Icon, t, d }) => (
              <div
                key={t}
                className="group relative bg-white rounded-2xl border border-[#E6ECF5] p-7 shadow-[0_8px_28px_-18px_rgba(11,36,71,0.25)] hover:shadow-[0_20px_50px_-20px_rgba(11,36,71,0.35)] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-light/15 to-brand-dark/10 text-brand-dark flex items-center justify-center group-hover:from-brand-light group-hover:to-brand-dark group-hover:text-white transition-colors duration-300">
                  <Icon className="h-5 w-5" strokeWidth={2.2} />
                </div>
                <h3 className="mt-5 font-display font-bold text-brand-dark text-[18px]">
                  {t}
                </h3>
                <span aria-hidden className="mt-2 block h-[2px] w-10 rounded-full bg-[color:var(--brand-red)]/85" />
                <p className="mt-3 text-[14px] text-muted-foreground leading-relaxed">
                  {d}
                </p>
              </div>
            ))}
          </div>

          <div className="rne-reveal mt-12 flex justify-center">
            <a
              href="#cotacao"
              className="inline-flex items-center gap-2 rounded-full bg-brand-dark hover:bg-brand text-white px-7 py-3.5 text-[14px] font-semibold shadow-[0_14px_34px_-12px_rgba(11,60,136,0.55)] hover:shadow-[0_20px_44px_-12px_rgba(11,60,136,0.75)] transition-all duration-300"
            >
              <Sparkles className="h-4 w-4" strokeWidth={2.4} />
              Quero enviar com a Rota Norte
              <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
            </a>
          </div>
        </div>
      </section>




      {/* COBERTURA — mapa ao vivo */}
      <section id="cobertura" className="py-20 sm:py-24 bg-[#F4F7FC]">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
          <div className="rne-reveal text-center max-w-2xl mx-auto">
            <span className="eyebrow">Cobertura ao vivo</span>
            <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
              Onde <span className="text-brand">atendemos</span>
            </h2>
            <p className="mt-4 text-[14.5px] text-muted-foreground leading-relaxed">
              Atendemos <strong className="text-brand-dark">todas as cidades ao longo da BR-364 e da BR-319</strong>, entre Vilhena, Porto Velho, Rio Branco e Manaus.
            </p>
          </div>
          <div className="mt-12">
            <CoverageMap />
          </div>

          <div className="rne-reveal mt-12 flex justify-center">
            <a
              href="#cotacao"
              className="inline-flex items-center gap-2 rounded-full bg-brand-dark hover:bg-brand text-white px-7 py-3.5 text-[14px] font-semibold shadow-[0_14px_34px_-12px_rgba(11,60,136,0.55)] hover:shadow-[0_20px_44px_-12px_rgba(11,60,136,0.75)] transition-all duration-300"
            >
              <MapPin className="h-4 w-4" strokeWidth={2.4} />
              Cotar envio para minha cidade
              <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
            </a>
          </div>
        </div>
      </section>


      {/* RASTREIO */}
      <section id="rastreio" className="py-20 sm:py-24">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
          <div className="rne-reveal text-center max-w-2xl mx-auto">
            <span className="eyebrow">Rastreio</span>
            <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
              Acompanhe sua encomenda <span className="text-brand">em tempo real</span>
            </h2>
            <p className="mt-4 text-[14.5px] text-muted-foreground leading-relaxed">
              Consulte o status atualizado da sua entrega direto pelo WhatsApp,
              a qualquer momento.
            </p>
          </div>

          <TrackingStepper />
        </div>
      </section>

      {/* COTAÇÃO — seção estratégica após o usuário entender como funciona */}
      <section id="cotacao" className="relative py-20 sm:py-24 scroll-mt-24 overflow-hidden">
        <PremiumDarkBg glow="right" />
        <div className="relative mx-auto max-w-[1180px] px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
          <div className="text-white text-center lg:text-left">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.2} />
              Cote em 30 segundos
            </span>
            <h2 className="mt-5 font-display text-[30px] sm:text-[36px] lg:text-[44px] font-extrabold leading-[1.05] tracking-tight">
              Envie sua encomenda com <span className="text-brand-light">tranquilidade</span>
            </h2>
            <p className="mt-5 text-[15px] text-white/75 leading-relaxed max-w-lg mx-auto lg:mx-0">
              Preencha origem, destino e o tipo de encomenda. Nossa equipe recebe seu pedido e retorna
              com o valor e todos os detalhes pelo WhatsApp.
            </p>
            <ul className="mt-6 space-y-2.5 inline-block text-left mx-auto lg:mx-0">
              {["Sem cadastro, sem burocracia", "Cotação em minutos, das 7h às 20h", "Seguro incluso em toda encomenda"].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[14px] text-white/85">
                  <CheckCircle2 className="h-4 w-4 text-brand-light mt-0.5 shrink-0" strokeWidth={2.4} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <QuickShippingCard />
        </div>
      </section>







      {/* FAQ */}
      <FaqSection
        items={[
          {
            q: "O que não pode ser enviado?",
            a: "Não transportamos itens ilegais, inflamáveis, explosivos, armas, munições, drogas, produtos perecíveis sem embalagem térmica adequada, dinheiro em espécie, joias de alto valor sem declaração e animais vivos. Em caso de dúvida, confirme conosco pelo WhatsApp antes de despachar.",
          },
          {
            q: "Como devo embalar itens frágeis?",
            a: "Utilize caixa de papelão resistente, com preenchimento interno (plástico bolha, isopor ou papel amassado) que impeça o movimento do item. Vede bem com fita larga e sinalize claramente a palavra 'FRÁGIL' em pelo menos duas faces da caixa.",
          },
          {
            q: "Até que horário posso postar para sair no mesmo dia?",
            a: "Encomendas postadas até as 13h seguem no mesmo dia. Postagens após esse horário são embarcadas no próximo trajeto disponível.",
          },
          {
            q: "Como funciona o seguro em caso de extravio?",
            a: "Toda encomenda transportada é acompanhada de registro interno e cobertura em caso de extravio comprovado. O valor de ressarcimento considera o item declarado no momento do envio — por isso é importante informar corretamente o conteúdo e o valor da mercadoria ao contratar o serviço.",
          },
          {
            q: "Existe limite de peso ou tamanho para a encomenda?",
            a: "Trabalhamos com volumes variados, de envelopes de documentos a cargas maiores. Para volumes acima de 30 kg ou com dimensões grandes, confirme conosco pelo WhatsApp antes do envio para avaliarmos a rota e o veículo mais adequado.",
          },
          {
            q: "Como acompanho o status da minha encomenda?",
            a: "Sua encomenda passa por três estágios: coletado (recebida pela nossa equipe), em rota (embarcada no veículo em direção ao destino) e entregue (confirmada com o destinatário). Nossa equipe informa cada mudança pelo mesmo WhatsApp usado na contratação.",
          },
        ]}
      />



      <PageFooter />
    </main>
  );
}

function HowItWorksStepper() {
  const steps = [
    {
      i: WhatsappIcon,
      t: "Fale pelo WhatsApp",
      d: "Informe origem, destino e o que precisa enviar. Retornamos com o valor e os detalhes.",
      accent: "#25D366",
    },
    {
      i: Package,
      t: "Prepare a encomenda",
      d: "Combinamos a coleta ou o local de embarque na cidade mais próxima da rota.",
      accent: "#0B3C88",
    },
    {
      i: Truck,
      t: "Acompanhe a entrega",
      d: "Sua encomenda segue com segurança e você recebe confirmação ao final.",
      accent: "#0B3C88",
    },
  ];

  return (
    <div className="rne-reveal mt-12 bg-white rounded-3xl border border-slate-100 p-6 sm:p-10 shadow-[0_20px_50px_-20px_rgba(7,28,58,0.18)]">
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3 mb-10">
        <div>
          <span className="eyebrow text-brand-light">Passo a passo</span>
          <h3 className="mt-2 font-display text-[22px] sm:text-[24px] font-bold tracking-tight text-brand-dark">
            Do primeiro contato até a entrega
          </h3>
          <p className="mt-1 text-[13px] text-slate-500 max-w-[460px]">
            Um fluxo simples e conhecido — cada etapa já pronta para dar
            continuidade à próxima.
          </p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] border-[#16A34A]/25 bg-[#16A34A]/10 text-[#16A34A]">
          <Check className="h-3 w-3" strokeWidth={3} />
          Processo validado
        </span>
      </div>

      <ol className="relative flex flex-col sm:flex-row sm:items-start gap-8 sm:gap-0 sm:px-4">
        {steps.map(({ i: Icon, t, d, accent }, idx) => {
          const isLast = idx === steps.length - 1;
          return (
            <li
              key={t}
              className="relative flex sm:flex-col items-start sm:items-center gap-4 sm:gap-3 sm:flex-1 sm:px-3 pl-0"
            >
              {/* MOBILE — conector vertical até o próximo passo */}
              {!isLast && (
                <span
                  aria-hidden
                  className="sm:hidden absolute left-5 top-10 h-[calc(100%-2.5rem+2rem)] w-[3px] bg-gradient-to-b from-[#42A5F5] to-[#16A34A]"
                  style={{ transform: "translateX(-50%)" }}
                />
              )}
              {/* DESKTOP — conector horizontal até o próximo passo */}
              {!isLast && (
                <span
                  aria-hidden
                  className="hidden sm:block absolute top-[17px] h-1.5 bg-gradient-to-r from-[#42A5F5] to-[#16A34A]"
                  style={{ left: "calc(50% + 24px)", width: "calc(100% - 48px)" }}
                />
              )}

              <div
                className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white shadow-[0_6px_18px_-6px_rgba(11,36,71,0.45)]"
                style={{ backgroundColor: accent }}
              >
                <Icon className="h-[18px] w-[18px]" strokeWidth={2.2} />
                <span
                  className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#16A34A] ring-2 ring-white"
                  aria-label="Etapa concluída"
                >
                  <Check className="h-2.5 w-2.5 text-white" strokeWidth={3.5} />
                </span>
              </div>

              <div className="sm:text-center">
                <div className="text-[10px] font-bold tracking-[0.22em] text-brand-light uppercase">
                  Passo {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="mt-1 font-display font-bold text-brand-dark text-[16px]">
                  {t}
                </div>
                <p className="mt-1.5 text-[13px] text-slate-500 leading-relaxed sm:max-w-[220px] sm:mx-auto">
                  {d}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}


function TrackingStepper() {
  const steps = [
    { i: PackageCheck, t: "Coletado", d: "Recebemos sua encomenda." },
    { i: Truck, t: "Em rota", d: "A caminho do destino." },
    { i: PackageSearch, t: "Entregue", d: "Confirmação de entrega." },
  ];

  const BAR_MS = 1800;
  const DWELL_MS = 900;
  const INITIAL_MS = 250;
  const RESTART_MS = 3200;

  // Fases: 0 = vazio (barra cinza), 1 = Coletado atingido, 2 = Em rota, 3 = Entregue.
  const TOTAL_PHASES = steps.length + 1; // 4
  const [targetPhase, setTargetPhase] = useState(0);
  const [displayedPhase, setDisplayedPhase] = useState(0);
  const [cycle, setCycle] = useState(0);

  // Passo "alcançado" = displayedPhase - 1 (idx do último status pintado).
  const reachedIdx = displayedPhase - 1;
  const isDelivered = reachedIdx === steps.length - 1;
  const isEmpty = reachedIdx < 0;
  const accentColor = isEmpty ? "#94A3B8" : isDelivered ? "#16A34A" : "#42A5F5";

  useEffect(() => {
    if (targetPhase === displayedPhase) return;
    const id = window.setTimeout(() => setDisplayedPhase(targetPhase), BAR_MS);
    return () => window.clearTimeout(id);
  }, [targetPhase, displayedPhase]);

  useEffect(() => {
    if (displayedPhase !== targetPhase) return;
    const atEnd = displayedPhase === TOTAL_PHASES - 1;
    const isStart = displayedPhase === 0;
    const isEnRoute = displayedPhase === 2;
    const delay = atEnd
      ? RESTART_MS
      : isStart
        ? INITIAL_MS
        : isEnRoute
          ? DWELL_MS + 2600
          : DWELL_MS;
    const id = window.setTimeout(() => {
      if (atEnd) {
        setDisplayedPhase(0);
        setTargetPhase(0);
        setCycle((c) => c + 1);
      } else {
        setTargetPhase((prev) => prev + 1);
      }
    }, delay);
    return () => window.clearTimeout(id);
  }, [displayedPhase, targetPhase, TOTAL_PHASES]);

  // Fração da barra preenchida por fase (0 → Coletado → Em rota → Entregue).
  const phaseFractions = [0, 0.16, 0.58, 1];
  const frac = phaseFractions[targetPhase];

  // Cor do ícone: cinza (vazio) até a barra chegar. Quando alcançado → azul,
  // exceto o passo final que fica verde.
  const stateColorFor = (idx: number) => {
    if (idx > reachedIdx) return "#CBD5E1"; // vazio
    if (idx === steps.length - 1) return "#16A34A"; // entregue
    return "#42A5F5";
  };

  const statusLabel = isEmpty ? "Aguardando coleta" : steps[reachedIdx].t;

  return (
    <div className="mt-14 bg-white rounded-3xl border border-slate-100 p-6 sm:p-10 shadow-[0_20px_50px_-20px_rgba(7,28,58,0.18)]">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3 mb-10">
        <div>
          <span className="eyebrow text-brand-light">Como consultar</span>
          <h3 className="mt-2 font-display text-[22px] sm:text-[24px] font-bold tracking-tight text-brand-dark">
            Rastreio direto no WhatsApp
          </h3>
          <p className="mt-1 text-[13px] text-slate-500 max-w-[420px]">
            Para saber em qual etapa a sua encomenda está, fale com a gente
            no WhatsApp e informamos o status atualizado.
          </p>
        </div>
        <span
          className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors duration-500"
          style={{
            borderColor: `${accentColor}33`,
            backgroundColor: `${accentColor}12`,
            color: accentColor,
          }}
        >
          <span
            className="h-1.5 w-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: accentColor }}
          />
          {statusLabel}
        </span>
      </div>

      {/* Stepper */}
      <ol className="relative flex flex-col sm:flex-row sm:items-start gap-8 sm:gap-0 sm:px-4">
        {/* MOBILE vertical rail (com runway antes do primeiro ícone) */}
        <span
          aria-hidden
          className="sm:hidden absolute left-5 top-0 bottom-5 w-[3px] bg-slate-100 rounded-full"
        />
        <span
          key={`m-${cycle}`}
          aria-hidden
          className="sm:hidden absolute left-5 top-0 w-[3px] rounded-full transition-[height,background-color] duration-[1800ms] ease-linear"
          style={{
            height: `calc((100% - 1.25rem) * ${frac})`,
            background: isEmpty
              ? "#CBD5E1"
              : `linear-gradient(to bottom, #42A5F5, ${accentColor})`,
          }}
        />

        {/* DESKTOP horizontal rail (com runway à esquerda) */}
        <span
          aria-hidden
          className="hidden sm:block absolute top-5 h-1.5 bg-slate-100 rounded-full"
          style={{ left: "calc(4% + 1rem)", right: "calc(16.6667% + 1rem)" }}
        />
        <span
          key={`d-${cycle}`}
          aria-hidden
          className="hidden sm:block absolute top-5 h-1.5 rounded-full transition-[width,background-color] duration-[1800ms] ease-linear"
          style={{
            left: "calc(4% + 1rem)",
            width: `calc((100% - 4% - 16.6667% - 2rem) * ${frac})`,
            background: isEmpty
              ? "#CBD5E1"
              : `linear-gradient(to right, #42A5F5, ${accentColor})`,
          }}
        />


        {steps.map(({ i: Icon, t, d }, idx) => {
          const active = idx === reachedIdx;
          const done = idx < reachedIdx;
          const nodeColor = stateColorFor(idx);
          const isReached = active || done;

          return (
            <li
              key={t}
              className="relative flex-1 flex sm:flex-col items-start sm:items-center text-left sm:text-center gap-4 sm:gap-0"
            >
              <span className="relative shrink-0">
                {active && (
                  <span
                    aria-hidden
                    className="absolute inset-0 rounded-full opacity-40 animate-ping"
                    style={{ backgroundColor: nodeColor }}
                  />
                )}
                <span
                  className="relative z-10 h-10 w-10 rounded-full bg-white flex items-center justify-center transition-all duration-500"
                  style={{
                    border: `3px solid ${nodeColor}`,
                    color: nodeColor,
                    boxShadow: active
                      ? `0 0 0 4px ${nodeColor}22, 0 8px 20px -10px ${nodeColor}80`
                      : "0 2px 6px -2px rgba(11,36,71,0.15)",
                    transform: active ? "scale(1.08)" : "scale(1)",
                  }}
                >
                  <Icon className="h-[18px] w-[18px]" strokeWidth={2.4} />
                </span>
              </span>
              <div className="sm:mt-5 flex-1 sm:flex-none">
                <div
                  className={`font-display font-semibold text-[14px] transition-colors duration-500 ${
                    isReached ? "text-brand-dark" : "text-slate-400"
                  }`}
                >
                  {t}
                </div>
                <div
                  className={`text-[11.5px] mt-1 transition-colors duration-500 ${
                    isReached ? "text-slate-500" : "text-slate-400"
                  }`}
                >
                  {d}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {/* Footer */}
      <div className="mt-10 pt-6 border-t border-slate-100 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-[color:var(--whatsapp)]/10 flex items-center justify-center shrink-0">
          <MessageCircle className="w-4 h-4 text-[color:var(--whatsapp)]" strokeWidth={2.4} />
        </div>
        <div className="text-[12px] text-slate-600 leading-snug">
          <strong className="text-brand-dark font-semibold">Consulta apenas pelo WhatsApp.</strong>{" "}
          Basta nos chamar que respondemos com o status atual da entrega.
          <div className="text-[11px] text-slate-400 mt-0.5">Atendimento 7h–20h</div>
        </div>
      </div>
    </div>
  );

}

/* FAQ estático — respostas sempre visíveis */
function FaqSection({ items }: { items: { q: string; a: string }[] }) {
  return (
    <section id="faq" className="py-20 sm:py-24">
      <div className="mx-auto max-w-[900px] px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <span className="eyebrow">Tire suas dúvidas</span>
          <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
            Perguntas frequentes
          </h2>
        </div>

        <div className="mt-10 space-y-3">
          {items.map((it, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-[#E6ECF5] shadow-[0_8px_28px_-18px_rgba(11,36,71,0.18)] overflow-hidden"
            >
              <div className="flex items-start gap-4 px-5 sm:px-6 pt-5">
                <span className="shrink-0 h-9 w-9 rounded-full bg-brand-light/10 text-brand-dark flex items-center justify-center mt-0.5">
                  <Plus className="h-4 w-4" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-bold text-brand-dark text-[15px] sm:text-[16px] leading-snug">
                  {it.q}
                </h3>
              </div>
              <p className="px-5 sm:px-6 pb-6 pt-3 pl-[calc(1.25rem+2.25rem+1rem)] sm:pl-[calc(1.5rem+2.25rem+1rem)] text-[14px] text-muted-foreground leading-relaxed">
                {it.a}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* Vertical journey — same visual language as the home routes */
function RouteAnimatedCard({
  idx,
  tag,
  title,
  sub,
  data,
}: {
  idx: number;
  tag: string;
  title: string;
  sub: string;
  data: Array<{ c: string; s: string; hl?: "start" | "end" }>;
}) {
  const n = data.length;
  const step = 32;
  const segs = n - 1;
  const goSpan = 46;
  const rotSpan = 4;
  const retSpan = 46;
  const segGo = goSpan / segs;
  const segRet = retSpan / segs;
  const dwellRatio = 0.45;
  const animName = `enc-route-car-${idx}`;
  const trailName = `enc-route-trail-${idx}`;
  const frames: string[] = [];
  const trailFrames: string[] = [];
  trailFrames.push(`0% { height: 0px; top: 16px; }`);
  for (let i = 0; i < n; i++) {
    const arrive = i * segGo;
    const y = i * step;
    frames.push(`${arrive.toFixed(2)}% { transform: translate(-50%, ${y}px) rotate(0deg); }`);
    if (i > 0) trailFrames.push(`${arrive.toFixed(2)}% { height: ${y}px; top: 16px; }`);
    if (i < n - 1) {
      const leave = arrive + segGo * dwellRatio;
      frames.push(`${leave.toFixed(2)}% { transform: translate(-50%, ${y}px) rotate(0deg); }`);
      if (i > 0) trailFrames.push(`${leave.toFixed(2)}% { height: ${y}px; top: 16px; }`);
    }
  }
  const endY = segs * step;
  frames.push(
    `${(goSpan + rotSpan).toFixed(2)}% { transform: translate(-50%, ${endY}px) rotate(180deg); }`,
  );
  trailFrames.push(`${(goSpan + rotSpan).toFixed(2)}% { height: ${endY}px; top: 16px; }`);
  const retStart = goSpan + rotSpan;
  trailFrames.push(`${(retStart + 0.01).toFixed(2)}% { height: 0px; top: ${16 + endY}px; }`);
  for (let k = 1; k <= n - 1; k++) {
    const i = n - 1 - k;
    const arrive = retStart + k * segRet;
    const y = i * step;
    frames.push(`${arrive.toFixed(2)}% { transform: translate(-50%, ${y}px) rotate(180deg); }`);
    const trailH = (segs - i) * step;
    trailFrames.push(`${arrive.toFixed(2)}% { height: ${trailH}px; top: ${16 + i * step}px; }`);
    if (k < n - 1) {
      const leave = arrive + segRet * dwellRatio;
      frames.push(`${leave.toFixed(2)}% { transform: translate(-50%, ${y}px) rotate(180deg); }`);
      trailFrames.push(`${leave.toFixed(2)}% { height: ${trailH}px; top: ${16 + i * step}px; }`);
    }
  }
  frames.push(`100% { transform: translate(-50%, 0px) rotate(360deg); }`);
  trailFrames.push(`100% { height: ${endY}px; top: 16px; }`);
  const duration = Math.max(28, n * 4);

  return (
    <div className="bg-[#0B1C40] rounded-3xl shadow-[0_40px_100px_-30px_rgba(6,18,43,0.55)] p-6 sm:p-7 border border-white/10">
      <style>{`@keyframes ${animName} { ${frames.join(" ")} } @keyframes ${trailName} { ${trailFrames.join(" ")} }`}</style>
      <div className="flex items-center gap-2.5">
        <span className="h-9 w-9 rounded-full bg-brand-light/15 border border-brand-light/30 flex items-center justify-center shrink-0">
          <MapPin className="h-4 w-4 text-brand-light" />
        </span>
        <div className="text-[11px] font-semibold text-white/60 uppercase tracking-[0.22em]">
          {tag}
        </div>
      </div>
      <h3 className="mt-3 font-display text-[22px] sm:text-[24px] font-extrabold text-white leading-tight">
        {title}
      </h3>
      <p className="mt-1 text-[12px] text-white/55 uppercase tracking-[0.18em] font-semibold">
        {sub}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-brand-light/10 border border-brand-light/25 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brand-light">
          <ArrowLeftRight className="h-3 w-3" strokeWidth={2.4} />
          Ida e volta
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] border border-white/15 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-white/75">
          <MapPin className="h-3 w-3" strokeWidth={2.4} />
          Coleta e entrega em todas as cidades
        </span>
      </div>




      <div className="mt-4 space-y-0 relative">
        {data.map((p, i, arr) => (
          <div key={p.c} className="flex items-stretch gap-3.5 h-8">
            <div className="flex flex-col items-center w-4 shrink-0 relative">
              {i > 0 && <div className="w-px flex-1 bg-white/20" />}
              <div
                className={
                  p.hl === "start"
                    ? "w-3 h-3 rounded-full border-2 border-[color:var(--whatsapp)] bg-transparent shadow-[0_0_0_3px_rgba(37,211,102,0.15)] shrink-0"
                    : p.hl === "end"
                      ? "w-3 h-3 rounded-full bg-brand-light ring-[3px] ring-brand-light/30 shrink-0"
                      : "w-2 h-2 rounded-full border border-white/45 bg-transparent shrink-0"
                }
              />
              {i < arr.length - 1 && <div className="w-px flex-1 bg-white/20" />}
            </div>
            <div className="flex-1 flex items-center justify-between">
              <span className="text-white font-medium text-[13.5px]">{p.c}</span>
              <span className="text-[10.5px] text-white/45 tracking-wider">{p.s}</span>
            </div>
          </div>
        ))}
        <span
          aria-hidden
          className="pointer-events-none absolute rounded-full bg-gradient-to-b from-brand-light to-[color:var(--whatsapp)] shadow-[0_0_10px_rgba(66,165,245,0.6)]"
          style={{
            left: "7px",
            width: "2px",
            top: "16px",
            height: "0px",
            animation: `${trailName} ${duration}s linear infinite`,
            willChange: "height, top",
          }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute flex items-center justify-center drop-shadow-[0_4px_10px_rgba(66,165,245,0.55)]"
          style={{
            left: "8px",
            top: "2px",
            animation: `${animName} ${duration}s linear infinite`,
            willChange: "transform",
          }}
        >
          <svg viewBox="0 0 22 36" className="h-7 w-[18px]" xmlns="http://www.w3.org/2000/svg">
            <rect
              x="3"
              y="2"
              width="16"
              height="32"
              rx="4"
              fill="#0B1C40"
              stroke="#42A5F5"
              strokeWidth="1"
            />
            <rect x="5" y="5" width="12" height="6" rx="1.5" fill="#FFD66B" />
            <rect x="5" y="14" width="12" height="9" rx="1.5" fill="#1E3A8A" />
            <rect
              x="5"
              y="26"
              width="12"
              height="5"
              rx="1.5"
              fill="#0B1C40"
              stroke="#42A5F5"
              strokeWidth="0.6"
            />
            <rect x="1.5" y="7" width="2" height="5" rx="0.6" fill="#0B1C40" />
            <rect x="18.5" y="7" width="2" height="5" rx="0.6" fill="#0B1C40" />
            <rect x="1.5" y="24" width="2" height="5" rx="0.6" fill="#0B1C40" />
            <rect x="18.5" y="24" width="2" height="5" rx="0.6" fill="#0B1C40" />
          </svg>
        </span>
      </div>
    </div>
  );
}

/* ---------- Inline lead card — matches passagens page pattern ---------- */
const CIDADES_ENCOMENDA = [
  "Vilhena (RO)",
  "Pimenta Bueno (RO)",
  "Cacoal (RO)",
  "Ji-Paraná (RO)",
  "Ouro Preto do Oeste (RO)",
  "Jaru (RO)",
  "Ariquemes (RO)",
  "Porto Velho (RO)",
  "Humaitá (AM)",
  "Manaus (AM)",
  "Rio Branco (AC)",
  "Outros",
];

const TIPOS_ENCOMENDA_INLINE = [
  "Documentos / Envelope",
  "Caixa pequena (até 5kg)",
  "Caixa média (5–15kg)",
  "Caixa grande (acima de 15kg)",
  "Autopeças",
  "Eletrônicos",
  "Medicamentos",
  "Vestuário",
  "Outros",
];

function formatPhoneEnc(raw: string) {
  const digits = raw.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}
const isValidPhoneEnc = (v: string) => v.replace(/\D/g, "").length >= 10;
const isValidNameEnc = (v: string) => v.trim().length >= 2;

function QuickShippingCard() {
  const [origem, setOrigem] = useState("");
  const [origemOutro, setOrigemOutro] = useState("");
  const [destino, setDestino] = useState("");
  const [destinoOutro, setDestinoOutro] = useState("");
  const [tipo, setTipo] = useState("");
  const [tipoOutro, setTipoOutro] = useState("");
  const [nome, setNome] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolvedOrigem = origem === "Outros" ? origemOutro.trim() : origem;
  const resolvedDestino = destino === "Outros" ? destinoOutro.trim() : destino;
  const resolvedTipo = tipo === "Outros" ? tipoOutro.trim() : tipo;

  const canSubmit =
    resolvedOrigem.length >= 2 &&
    resolvedDestino.length >= 2 &&
    resolvedOrigem !== resolvedDestino &&
    resolvedTipo.length >= 2 &&
    nome.trim().length >= 2;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const tracking = captureTracking();
      const payload = {
        kind: "encomenda" as const,
        nome: nome.trim(),
        telefone: null,
        email: null,
        origem: resolvedOrigem,
        destino: resolvedDestino,
        tipo_encomenda: resolvedTipo,
        quantidade: null,
        source_path: typeof window !== "undefined" ? window.location.pathname : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        utm_source: tracking.utm_source ?? null,
        utm_medium: tracking.utm_medium ?? null,
        utm_campaign: tracking.utm_campaign ?? null,
        utm_content: tracking.utm_content ?? null,
        utm_term: tracking.utm_term ?? null,
        gclid: tracking.gclid ?? null,
        fbclid: tracking.fbclid ?? null,
        referrer: tracking.referrer ?? null,
        status: "Novo Lead",
      };

      const [{ supabase }, { dispatchLead }, { saveLeadToDrive }] = await Promise.all([
        import("@/integrations/supabase/client"),
        import("@/lib/lead-dispatch"),
        import("@/lib/leads-drive.functions"),
      ]);
      const { error: dbError } = await supabase.from("leads").insert(payload as never);
      if (dbError) throw dbError;

      const summary: LeadSummary = {
        kind: "encomenda",
        nome: payload.nome,
        telefone: payload.telefone ?? "",
        email: null,
      };
      saveLastLead(summary);

      try {
        const w = window as unknown as {
          dataLayer?: unknown[];
          gtag?: (...a: unknown[]) => void;
        };
        w.dataLayer = w.dataLayer ?? [];
        w.dataLayer.push({ event: "generate_lead_encomenda" });
        if (typeof w.gtag === "function") w.gtag("event", "generate_lead_encomenda");
      } catch { /* noop */ }

      void dispatchLead({
        ...summary,
        ...tracking,
        source_path: payload.source_path,
        user_agent: payload.user_agent,
      });

      void saveLeadToDrive({
        data: {
          kind: "encomenda",
          nome: payload.nome,
          telefone: payload.telefone ?? "",
          email: null,
          origem: payload.origem,
          destino: payload.destino,
          quantidade: null,
          tipo_encomenda: payload.tipo_encomenda,
          source_path: payload.source_path,
          user_agent: payload.user_agent,
          utm_source: payload.utm_source,
          utm_medium: payload.utm_medium,
          utm_campaign: payload.utm_campaign,
          utm_content: payload.utm_content,
          utm_term: payload.utm_term,
          gclid: payload.gclid,
          fbclid: payload.fbclid,
          referrer: payload.referrer,
        },
      }).catch((err) => console.warn("[QuickShipping] drive save failed", err));

      const msg = `👋 Olá! Meu nome é ${nome.trim()}.\n\n📦 *Quero enviar uma encomenda* pela Rota Norte Express.\n\n📍 Origem: ${resolvedOrigem}\n🎯 Destino: ${resolvedDestino}\n🏷️ Tipo: ${resolvedTipo}\n\nPoderia me ajudar com a cotação? 🙌`;
      const url = `https://api.whatsapp.com/send?phone=${WA_NUMBER}&text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("[QuickShipping] failed", err);
      setError("Não foi possível enviar sua solicitação. Tente novamente em instantes.");
      setSubmitting(false);
    }
  }

  return (
    <div className="relative rne-hero-in">
      <div
        aria-hidden
        className="rne-card-glow absolute -inset-4 rounded-[36px] bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.55),_rgba(59,130,246,0)_70%)] blur-2xl"
      />
      <div
        aria-hidden
        className="rne-card-ring pointer-events-none absolute -inset-[3px] rounded-[30px] border-2 border-[rgba(59,130,246,0.7)]"
      />
      <form
        onSubmit={handleSubmit}
        className="relative bg-white rounded-[28px] border border-white/10 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.55)] p-6 sm:p-8"
      >
        <span className="absolute -top-3 right-6 inline-flex items-center gap-2 rounded-full bg-white border border-[#E4E9F2] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-dark shadow-[0_8px_20px_-10px_rgba(11,36,71,0.35)]">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 animate-ping [animation-duration:2.4s]" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          Cotação em tempo real
        </span>

        <div className="flex items-start gap-3 pb-5 border-b border-[#EEF2F8]">
          <span className="h-10 w-10 rounded-lg bg-brand-dark text-white flex items-center justify-center shrink-0">
            <Package className="h-[18px] w-[18px]" strokeWidth={2.2} />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand">
              Envio de encomenda
            </div>
            <div className="mt-0.5 font-display font-bold text-brand-dark text-[18px] leading-tight">
              Cote o envio da sua encomenda
            </div>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <FieldSelectEnc
            label="Origem"
            icon={<MapPin className="h-4 w-4" />}
            value={origem}
            onChange={setOrigem}
            options={CIDADES_ENCOMENDA}
            placeholder="De qual cidade você envia?"
            excludeOption={destino}
          />
          {origem === "Outros" && (
            <input
              type="text"
              placeholder="Digite sua cidade de origem"
              value={origemOutro}
              onChange={(e) => setOrigemOutro(e.target.value)}
              className="w-full h-12 px-3 rounded-xl bg-[#F5F7FC] border border-[#E4E9F2] focus:border-brand focus:ring-4 focus:ring-brand/10 focus:bg-white outline-none text-[14.5px] text-foreground transition-all"
            />
          )}

          <FieldSelectEnc
            label="Destino"
            icon={<MapPin className="h-4 w-4" />}
            value={destino}
            onChange={setDestino}
            options={CIDADES_ENCOMENDA}
            placeholder="Para qual cidade?"
            excludeOption={origem}
          />
          {destino === "Outros" && (
            <input
              type="text"
              placeholder="Digite sua cidade de destino"
              value={destinoOutro}
              onChange={(e) => setDestinoOutro(e.target.value)}
              className="w-full h-12 px-3 rounded-xl bg-[#F5F7FC] border border-[#E4E9F2] focus:border-brand focus:ring-4 focus:ring-brand/10 focus:bg-white outline-none text-[14.5px] text-foreground transition-all"
            />
          )}

          <FieldSelectEnc
            label="Tipo de encomenda"
            icon={<Package className="h-4 w-4" />}
            value={tipo}
            onChange={setTipo}
            options={TIPOS_ENCOMENDA_INLINE}
            placeholder="O que você quer enviar?"
          />
          {tipo === "Outros" && (
            <input
              type="text"
              placeholder="Descreva o tipo de encomenda"
              value={tipoOutro}
              onChange={(e) => setTipoOutro(e.target.value)}
              className="w-full h-12 px-3 rounded-xl bg-[#F5F7FC] border border-[#E4E9F2] focus:border-brand focus:ring-4 focus:ring-brand/10 focus:bg-white outline-none text-[14.5px] text-foreground transition-all"
            />
          )}

          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-dark/70">Nome</span>
            <div className="relative mt-1.5">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                autoComplete="name"
                placeholder="Seu nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="w-full h-12 pl-10 pr-3 rounded-xl bg-[#F5F7FC] border border-[#E4E9F2] focus:border-brand focus:ring-4 focus:ring-brand/10 focus:bg-white outline-none text-[14.5px] text-foreground transition-all"
              />
            </div>
          </label>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit || submitting}
          className={`group relative mt-5 w-full inline-flex items-center justify-center gap-2.5 h-[56px] px-6 rounded-xl font-semibold text-[15px] overflow-hidden transition-all duration-300 ${
            canSubmit && !submitting
              ? "bg-gradient-to-b from-[#25D366] to-[#128C7E] text-white hover:-translate-y-[2px] shadow-[0_20px_48px_-14px_rgba(18,140,126,0.55)]"
              : "bg-[#E4E9F2] text-slate-500 cursor-not-allowed"
          }`}
        >
          {canSubmit && !submitting && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent rne-cta-sheen"
            />
          )}
          {submitting ? (
            <span className="relative inline-block h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
          ) : (
            <WhatsappIcon className="relative h-[19px] w-[19px]" />
          )}
          <span className="relative">
            {submitting ? "Enviando…" : "Obter cotação no WhatsApp"}
          </span>
          {canSubmit && !submitting && (
            <ArrowRight className="relative h-[16px] w-[16px] opacity-90 transition-transform group-hover:translate-x-1" strokeWidth={2.4} />
          )}
        </button>


        <div className="mt-4 flex items-center justify-center gap-2 text-[12px] text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5 text-brand" />
          <span>Cotação em segundos · Sem cadastro · Sem taxa</span>
        </div>
      </form>
    </div>
  );
}

function FieldSelectEnc({
  label,
  icon,
  value,
  onChange,
  options,
  placeholder,
  excludeOption,
}: {
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  excludeOption?: string;
}) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-dark/70">
        {label}
      </span>
      <div className="relative mt-1.5">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground">
          {icon}
        </span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full h-12 pl-10 pr-9 rounded-xl bg-[#F5F7FC] border border-[#E4E9F2] focus:border-brand focus:ring-4 focus:ring-brand/10 focus:bg-white outline-none text-[14.5px] text-foreground transition-all cursor-pointer"
        >
          <option value="" disabled>{placeholder}</option>
          {options.map((o) => (
            <option key={o} value={o} disabled={o === excludeOption}>{o}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      </div>
    </label>
  );
}




